import { memo, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useStore } from 'zustand';
import { DEFAULT_TIMESHEETS_SIZE, timesheetsService, timesheetsStore } from 'entities/timesheet';
import { MonthItem } from '../../lib/getMonthsInRange';
import { mapToTableData } from '../../lib/mapToTableData';
import { CustomColumn, TimesheetTableRow } from '../../TimesheetsTable.types';
import { TimesheetsTable } from '../TimesheetsTable';

const MonthPanelContent = ({
  monthItem,
  columnsVisibilityMap,
  allColumns,
  isExpanded,
}: {
  monthItem: MonthItem;
  columnsVisibilityMap: Record<string, boolean>;
  allColumns: CustomColumn<TimesheetTableRow>[];
  isExpanded: boolean;
}): React.ReactElement => {
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get('accountId');
  const projectId = searchParams.get('projectId');

  const topLevelFilters = useStore(timesheetsStore, (state) => state.topLevelFilters);
  const columnFilters = useStore(timesheetsStore, (state) => state.columnFilters);
  const orders = useStore(timesheetsStore, (state) => state.orders);

  const { data, isFetching } = useQuery(
    timesheetsService.queryOptions({
      year: monthItem.year.toString(),
      month: (monthItem.month + 1).toString(),
      size: DEFAULT_TIMESHEETS_SIZE,
      sort: orders?.[0]?.fieldName,
      order: orders?.[0]?.orderDirection,
      filters: {
        ...topLevelFilters,
        ...columnFilters,
        projectIds: projectId ? [projectId] : topLevelFilters.projectIds,
        accountIds: !projectId && accountId ? [accountId] : undefined,
      },
    }),
  );

  const tableData = useMemo(() => (data ? mapToTableData(data) : []), [data]);

  return (
    <TimesheetsTable
      tableData={tableData}
      columnsVisibilityMap={columnsVisibilityMap}
      allColumns={allColumns}
      isExpanded={isExpanded}
      isLoading={isFetching}
    />
  );
};

export default memo(MonthPanelContent);
